/**
 * Promotion helpers — applies LATAM-style discounts and bonifications
 * when adding a product to an order.
 *
 *   Discount: product.descuento_pct (0-100) reduces precio_unitario.
 *   Bonification: product.bonif_compra + product.bonif_gratis.
 *     For every `bonif_compra` units in the cart, the customer receives
 *     `bonif_gratis` units of the SAME product for free.
 *
 * Returns the list of OrderLine objects to push into the cart for that
 * single product entry. Typically returns 1 line (priced) or 2 lines
 * (priced + bonus). Callers should append these to the cart array as-is.
 *
 * The shape mirrors the OrderLine Pydantic model on the backend so the
 * cart can be POSTed verbatim.
 */
export function buildPromoLines(product, qty) {
  const cantidad = Math.max(1, parseInt(qty) || 1);
  const basePrice = parseFloat(product?.precio_venta) || 0;
  const descPct = Math.max(0, Math.min(100, parseFloat(product?.descuento_pct) || 0));
  const effectivePrice = +(basePrice * (1 - descPct / 100)).toFixed(2);

  const codigo = product?.codigo || "";
  const descripcion = product?.descripcion || "";
  const labelDesc = descPct > 0
    ? `${descripcion} (-${descPct}%)`
    : descripcion;

  const lines = [{
    codigo,
    cantidad,
    producto: labelDesc,
    precio_unitario: effectivePrice,
    subtotal: +(effectivePrice * cantidad).toFixed(2),
    es_bonificacion: false,
  }];

  const buy = parseInt(product?.bonif_compra) || 0;
  const free = parseInt(product?.bonif_gratis) || 0;
  if (buy > 0 && free > 0 && cantidad >= buy) {
    const bonusQty = Math.floor(cantidad / buy) * free;
    if (bonusQty > 0) {
      lines.push({
        codigo,
        cantidad: bonusQty,
        producto: `${descripcion} · BONIFICACIÓN GRATIS`,
        precio_unitario: 0,
        subtotal: 0,
        es_bonificacion: true,
      });
    }
  }
  return lines;
}

// Returns a short human label like "10% OFF · 12+1" for catalogs/badges.
// Empty string when the product has no promo.
export function promoLabel(product) {
  const parts = [];
  if (product?.descuento_pct > 0) parts.push(`${product.descuento_pct}% OFF`);
  if (product?.bonif_compra > 0 && product?.bonif_gratis > 0) {
    parts.push(`${product.bonif_compra}+${product.bonif_gratis} GRATIS`);
  }
  return parts.join(" · ");
}
